import { useCallback, useMemo } from "react";

import { useTypedSelector } from "../../redux/store";
import { mapByKey } from "../../utils";
import { useTaxiServiceFilterAccessControl } from "..";

import { filterTaxiServiceIdsByCompanies } from "./filterTaxiServiceIdsByCompanies";

import { useCompanyIdsDecoder } from ".";

export { filterTaxiServiceIdsByCompanies };

function useTaxiServiceIdsDecoder({
	allowedTaxiServiceIds,
}: useTaxiServiceIdsDecoder.Options = {}) {
	const decodeCompanyIds = useCompanyIdsDecoder();
	const { accessTaxiServicesIds } = useTaxiServiceFilterAccessControl<any>();

	const { models: taxiServices } = useTypedSelector(
		(state) => state.taxiServices,
	);

	const taxiServiceById = useMemo(
		() => mapByKey(taxiServices, "id"),
		[taxiServices],
	);

	const availableTaxiServicesIds = useMemo(
		() =>
			taxiServices
				.map((taxiService) => taxiService.id)
				.filter(
					(taxiServiceId) =>
						allowedTaxiServiceIds?.includes(taxiServiceId) ?? true,
				),
		[allowedTaxiServiceIds, taxiServices],
	);

	const isAllIds = useCallback(
		(
			taxiServices: useTaxiServiceIdsDecoder.EncodedIds,
		): taxiServices is ["all"] => taxiServices[0] === "all",
		[],
	);

	const decoder = useCallback(
		(
			companyIds: useTaxiServiceIdsDecoder.EncodedIds,
			taxiServiceIds: useTaxiServiceIdsDecoder.EncodedIds,
			options: useTaxiServiceIdsDecoder.DecoderOptions = {},
		) => {
			const isOnlyAccessTaxiServicesIds = options.isOnlyAccess
				? accessTaxiServicesIds
				: availableTaxiServicesIds;
			const isOnlyAccessCompaniesIds = options.isOnlyAccess
				? { isOnlyAccess: true }
				: {};

			const decodedCompanyIds = decodeCompanyIds(
				companyIds,
				isOnlyAccessCompaniesIds,
			);

			if (isAllIds(companyIds) && isAllIds(taxiServiceIds))
				return isOnlyAccessTaxiServicesIds;

			if (isAllIds(taxiServiceIds))
				return filterTaxiServiceIdsByCompanies(
					isOnlyAccessTaxiServicesIds,
					taxiServiceById,
					decodedCompanyIds,
				);

			return isOnlyAccessTaxiServicesIds.filter((taxiServiceId) =>
				taxiServiceIds.includes(taxiServiceId),
			);
		},
		[
			accessTaxiServicesIds,
			availableTaxiServicesIds,
			decodeCompanyIds,
			isAllIds,
			taxiServiceById,
		],
	);

	return decoder;
}

declare namespace useTaxiServiceIdsDecoder {
	interface Options {
		allowedTaxiServiceIds?: number[];
	}

	interface DecoderOptions {
		isOnlyAccess?: boolean;
	}

	type EncodedIds = number[] | ["all"];
}

export default useTaxiServiceIdsDecoder;
